import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { PatientDto, PatientVitalsDto, PatientVitalsResponseDto, UserDto } from '@dto';
import { PaginatedResponse as LegacyPaginatedResponse, Patient as LegacyPatient } from '@models';

import { environment } from '../../../environments/environment';
import { PaginationInformation } from '../../shared/server-side-paginated-table/datasources/generic.datasource';
import { Relationship } from '@enums';
import { UserService } from './user.service';
import { Patient as NewApiPatient, UpdatePatientRequest } from '../../react/api/model';
import { UUID } from '../../react/types';
import { SponsorshipService } from './sponsorship.service';
import { logError } from '../../react/utils';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  public readonly selectedPatient: Observable<LegacyPatient>;
  public readonly selectedPatientId: Observable<string>;

  private _selectedPatient = new BehaviorSubject<LegacyPatient>(null);
  private _selectedPatientId = new BehaviorSubject<string>(null);

  constructor(
    private httpClient: HttpClient,
    private userService: UserService,
    private sponsorshipService: SponsorshipService
  ) {
    this.selectedPatient = this._selectedPatient.asObservable();
    this.selectedPatientId = this._selectedPatientId.asObservable();

    this.selectedPatientId
      .pipe(
        switchMap((id) => {
          if (!id) {
            return of(null);
          }
          return this.getPatientDetailed(id);
        })
      )
      .subscribe((result) => {
        this._selectedPatient.next(result);
      });
  }

  public updateSelectedPatientId(id: string | null) {
    this._selectedPatientId.next(id);
  }

  /**
   * @deprecated Use new APIs
   */
  public searchForPatientByNameOrId(search: string) {
    let queryParams = new HttpParams({
      fromObject: {
        search,
        sortBy: 'first_name',
        sortDirection: 'ASC',
        limit: '10',
        page: '0',
      },
    });

    queryParams = queryParams.append('searchBy[]', 'id');
    queryParams = queryParams.append('searchBy[]', 'name');
    const showDeleted = 'false';
    return this.getLegacyPatientsPaginated(queryParams, {
      filters: { showDeleted },
    } as any);
  }

  /**
   * @deprecated Use new APIs
   */
  public searchForPatientById(search: string) {
    let queryParams = new HttpParams({
      fromObject: {
        search,
        sortBy: 'id',
        sortDirection: 'ASC',
        limit: '10',
        page: '0',
      },
    });

    queryParams = queryParams.append('searchBy[]', 'id');
    const showDeleted = 'false';
    return this.getLegacyPatientsPaginated(queryParams, {
      filters: { showDeleted },
    } as any);
  }

  /**
   * @deprecated Use new APIs
   */
  public searchForPatientByNameOrIdOrDobNav(search: string) {
    let queryParams = new HttpParams({
      fromObject: {
        search,
        sortBy: 'navSearch',
        sortDirection: 'ASC',
        limit: '10',
        page: '0',
      },
    });
    queryParams = queryParams.append('searchBy[]', 'dateOfBirth');
    queryParams = queryParams.append('searchBy[]', 'id');
    queryParams = queryParams.append('searchBy[]', 'name');
    const showDeleted = 'false';
    return this.getLegacyPatientsPaginated(queryParams, {
      filters: { showDeleted },
    } as any).pipe(
      map((res) =>
        res.data.map((patient) => ({
          ...patient,
          label: `#${patient.id} ${patient.firstName} ${patient.lastName}`,
        }))
      )
    );
  }

  /**
   * @deprecated Use new APIs
   */
  public getLegacyPatientsPaginated(queryParams: HttpParams, { filters }: PaginationInformation) {
    const url = environment.apiV3Url.concat('/patients');

    queryParams = queryParams.append('showDeleted', filters.showDeleted as string);

    return this.httpClient
      .get<LegacyPaginatedResponse<PatientDto>>(url, {
        params: queryParams,
      })
      .pipe(
        map((patients) => ({
          totalCount: patients.totalCount,
          data: patients.data.map((patientData) => {
            const patient = new LegacyPatient(patientData);

            const email =
              patient.user?.email || patient.email
                ? patient.user?.email || patient.email
                : (patient.membership &&
                    patient.membership.primaryAccountHolder &&
                    patient.membership.primaryAccountHolder.email) ||
                  '';
            const phone =
              patient.user?.phone || patient.phone
                ? patient.user?.phone || patient.phone
                : (patient.membership &&
                    patient.membership.primaryAccountHolder &&
                    patient.membership.primaryAccountHolder.phone) ||
                  '';
            const isPrimaryAccountHolder = patient.relationToAccountHolder === Relationship.Self;

            return {
              email,
              phone,
              isPrimaryAccountHolder,
              id: patient.id,
              firstName: patient.user.firstName,
              lastName: patient.user.lastName,
              sexAssignedAtBirth: patient.sexAssignedAtBirth,
              gender: patient.gender,
              dateOfBirth: patient.user.dateOfBirth,
              membershipId: patient.membership ? patient.membership.id : null,
              membershipTypeName:
                patient.membership && patient.membership.membershipType
                  ? patient.membership.membershipType.membershipName
                  : null,
              employerName:
                (patient.membership && patient.membership.company && patient.membership.company.name) || null,
              address1: patient.user.address ? patient.user.address.address1 : null,
              address2: patient.user.address ? patient.user.address.address2 : null,
              city: patient.user.address ? patient.user.address.city : null,
              zip: patient.user.address ? patient.user.address.zip : null,
              state: patient.user.address ? patient.user.address.state : null,
              membershipTypeId:
                patient.membership && patient.membership.membershipType ? patient.membership.membershipType.id : null,
              copayAmount:
                (patient.membership &&
                  patient.membership.membershipType &&
                  (patient.membership.membershipType.copayAmount as string)) ||
                null,
              active: patient.active,
              expired: patient.expired,
              deleted: patient.deleted,
              syncStatus: true,
              rawPatient: patient,
            };
          }),
        }))
      );
  }

  /**
   * @deprecated Use new APIs
   * Returns patients list with additional details re: their membership
   */
  public getPatientsByMembershipId(id: string | number) {
    const url = environment.apiV3Url.concat(`/memberships/${id}/patients`);
    return this.httpClient.get<PatientDto[]>(url).pipe(
      map((patients) =>
        patients.map((patient) => {
          return new LegacyPatient(patient);
        })
      )
    );
  }

  public getPatient(patientId: string | number) {
    const url = environment.apiUrl.concat(`/patient/${patientId}`);

    return this.httpClient.get<PatientDto>(url).pipe(map((patient) => new LegacyPatient(patient)));
  }

  public getPatientDetailed(patientId: string): Observable<LegacyPatient> {
    const url = environment.apiUrl.concat(`/patient/${patientId}/membershipDetail`);

    return this.httpClient.get<PatientDto>(url).pipe(
      map((patient: PatientDto) => {
        return new LegacyPatient(patient);
      })
    );
  }

  public async getPatientFromNewApi(patientId: number): Promise<NewApiPatient | null> {
    // Can be null, especially in cases like requesting a deleted user.
    return await this.userService.getPatientUser(patientId as number);
  }

  public async updatePatient(patient: Partial<PatientDto>): Promise<NewApiPatient> {
    const databasePatient = await this.userService.getPatientUser(patient.id as number);
    const url = `${environment.niceServiceUrl}/v2/users/${databasePatient.userId}`;

    const originalDateOfBirth = patient.user.dateOfBirth;
    const [month, day, year] = originalDateOfBirth.split('/');

    const formattedDateOfBirth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

    const updatePatientRequest: UpdatePatientRequest = {
      userId: databasePatient.userId,
      firstName: patient.user.firstName,
      preferredName: patient.user.preferredName,
      lastName: patient.user.lastName,
      dateOfBirth: formattedDateOfBirth,
      gender: patient.gender.toString(),
      address: {
        line1: patient.addresses[0].address1,
        line2: patient.addresses[0].address2,
        city: patient.addresses[0].city,
        state: patient.addresses[0].state,
        zip: patient.addresses[0].zip,
      },
      sexAssignedAtBirth: patient.sexAssignedAtBirth,
      email: patient.user?.email ? patient.user.email : patient.email,
      phone: patient.user?.phone ? patient.user.phone : patient.phone,
      race: patient.race.toString(),
      relationToAccountHolder: patient.relationToAccountHolder.toString(),
      verified: true, // No longer in use; set to default (true).
      deactivated: patient.deactivated,
      allow65Plus: false, // No longer in use; set to default (false).
      type: 'UpdateUserRequestV2::Provider::UpdatePatient::V1',
    };

    const updatedPatient = await this.httpClient.put<NewApiPatient>(url, updatePatientRequest).toPromise();

    if (!patient.deleted) {
      try {
        await this.sponsorshipService.markPatientSponsorshipEligibility(
          updatedPatient.userId as UUID,
          updatedPatient.deactivated
        );
      } catch (error) {
        logError(
          'Failed to mark patient ' +
            updatedPatient.userId +
            ' ' +
            (updatedPatient.deactivated ? 'ineligible' : 'eligible'),
          error
        );
      }
    }

    return updatedPatient;
  }

  public async deletePatient(patientId: number) {
    const patient = await this.userService.getPatientUser(patientId);
    if (patient !== undefined && patient !== null) {
      const url = environment.niceServiceUrl.concat(`/v1/users/${patient.userId}`);
      return this.httpClient.delete(url).toPromise();
    }
  }

  public async addPatientNote(note: string, patientId: number): Promise<NewApiPatient> {
    const patient = await this.userService.getPatientUser(patientId);
    const url = environment.niceServiceUrl.concat(`/v1/users/${patient.userId}/note`);
    return this.httpClient.post<NewApiPatient>(url, { text: note }).toPromise();
  }

  public addPatientVitals(vitals: PatientVitalsDto): Observable<PatientVitalsResponseDto> {
    const url = environment.apiV3Url.concat('/patient-vitals');

    return this.httpClient.post<PatientVitalsDto>(url, vitals);
  }

  public updatePatientVitals(vitals: PatientVitalsDto): Observable<PatientVitalsResponseDto> {
    const url = environment.apiV3Url.concat(`/patient-vitals/${vitals.id}`);

    return this.httpClient.put<PatientVitalsDto>(url, vitals);
  }

  public getPatientVitals(patientId: number): Observable<PatientVitalsResponseDto[]> {
    const url = environment.apiV3Url.concat('/patient-vitals');

    return this.httpClient.get<PatientVitalsResponseDto[]>(url, {
      params: new HttpParams().set('patientId', patientId.toString()),
    });
  }

  /**
   * @deprecated use getElationPatientLink(patientUserId)
   * @param patientId
   */
  public async getElationPatientLinkByPatientId(patientId: number): Promise<PatientElationLinkResponse> {
    const patient: NewApiPatient | null = await this.userService.getPatientUser(patientId);

    if (patient) {
      return this.getElationPatientLink(patient.userId as UUID);
    }

    // Unknown user response.
    return {
      isElationPatient: false,
      link: '',
    };
  }

  public async getElationPatientLink(patientUserId: UUID): Promise<PatientElationLinkResponse> {
    const url = environment.niceServiceUrl.concat(`/v1/elation/patients/${patientUserId}/link`);
    return await this.httpClient.get<PatientElationLinkResponse>(url).toPromise();
  }
  public async updatePatientElationId(patientId: number, elationId: string): Promise<any> {
    const patient = await this.userService.getPatientUser(patientId);
    const body = {
      elationId: elationId,
    };

    const url = environment.niceServiceUrl.concat(`/v1/users/${patient.userId}/elation`);
    return await this.httpClient.patch(url, body).toPromise();
  }
  public async getPatientUsersForMovingElationId(
    elationId: string,
    patientIdGainingElationId: number
  ): Promise<{
    patientLosingElationId: PatientElationSearchResponse;
    patientGainingElationId: PatientElationSearchResponse;
  } | null> {
    const existingPatientUsingElationId = await this.userService.getPatientUserByElationId(elationId);
    if (existingPatientUsingElationId) {
      const existingPatientGainingElationId = await this.userService.getPatientUser(patientIdGainingElationId);
      return existingPatientGainingElationId
        ? {
            patientLosingElationId: {
              id: existingPatientUsingElationId.patientId,
              firstName: existingPatientUsingElationId.firstName,
              lastName: existingPatientUsingElationId.lastName,
              dateOfBirth: existingPatientUsingElationId.dateOfBirth,
              userId: existingPatientUsingElationId.userId,
            },
            patientGainingElationId: {
              id: existingPatientGainingElationId.patientId,
              firstName: existingPatientGainingElationId.firstName,
              lastName: existingPatientGainingElationId.lastName,
              dateOfBirth: existingPatientGainingElationId.dateOfBirth,
              userId: existingPatientGainingElationId.userId,
            },
          }
        : null;
    }
  }
  public async moveExistingElationIdToPatient(fromUserId: UUID, toUserId: UUID): Promise<any> {
    const body = {
      fromUserId: fromUserId,
      toUserId: toUserId,
    };
    const url = environment.niceServiceUrl.concat('/v1/elation/move-elation-id');
    return await this.httpClient.post(url, body).toPromise();
  }

  public async syncPatientProfileAndAppointmentHistory(patientId: number): Promise<PatientElationSyncResponse> {
    const patient = await this.userService.getPatientUser(patientId);
    const body = {
      userId: patient.userId,
    };
    const url = environment.niceServiceUrl.concat('/v1/elation/force-sync-patient-appointments');
    const response = await this.httpClient.post<PatientElationSyncResponse>(url, body).toPromise();
    return {
      elationId: response.elationId,
      link: response.link,
    };
  }
}

export interface PatientElationSearchResponse {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  userId?: string;
  user?: UserDto;
}

interface PatientElationSyncResponse {
  elationId: string;
  link: string;
}

export interface PatientElationLinkResponse {
  isElationPatient: boolean;
  link: string;
}
